import type { ClientLocationPlanHydrated } from "api-types/locationPlans/locationPlansResponses";
import { isMultiUnitLocation } from "api-types/locations/locationDisplay";
import type { ClientMasterPlanHydrated } from "api-types/masterPlans/masterPlansResponses";
import { BuilderDocumentType } from "api-types/modelTypes";
import { assertIsNotNullOrUndefined, assertIsNotUndefined, ensureDefined } from "common/utils/assertOfType";
import { locationAncestorsForLocationId } from "lib/components/builders/communities/utils/locationUtils";
import {
  useExportMasterPlanEvents,
  useExportHomePlanEvents,
  useExportCommunityPlanEvents,
} from "lib/components/builders/communities/utils/useExportPlanEvents";
import { useLocationHierarchy } from "lib/components/builders/communities/utils/useLocationHierarchy";
import computeDefaultPlanSetExportFileName from "lib/components/design/defaultPlanSetExportFileName";
import ExportPlanSetTemplateModalContent from "lib/components/design/ExportPlanSetTemplateModalContent";
import DownloadProgressPlanModal from "lib/components/design/modal/DownloadProgressPlanModal";
import { PlanSetComputeService } from "lib/components/studio/editorViewer/planSetComputeService";
import type { DownloadInitializer } from "lib/hooks/useDownloadState";
import type { TitleSheetInfo } from "lib/house/plans/titleSheetInfo";
import { HomePlanContext } from "lib/state/HomePlanContextProvider";
import { useUserContext } from "lib/state/UserContextProvider";
import { getHydratedLocationPlan } from "lib/storage/locationPlanStorage";
import { getMasterPlanHydrated } from "lib/storage/masterPlanStorage";
import type {
  BasePlanSetDownloadParams,
  DownloadCommunityPlanModalProps,
  DownloadCommunityPlanParams,
  DownloadHomePlanModalProps,
  DownloadHomePlanParams,
  DownloadMasterPlanModalParams,
  DownloadMasterPlanParams,
  DownloadPlanSetTemplateParams,
} from "lib/workers/pdfDownloads";
import { useContext, useEffect, useMemo, useState } from "react";
import DownloadPlanModalContextProvider, { useDownloadPlanModalContext } from "./DownloadPlanModalContextProvider";

const downloadHomePlan: DownloadInitializer<DownloadHomePlanParams | DownloadPlanSetTemplateParams> = async (
  params,
) => {
  const mod = await import("lib/workers/pdfDownloads");
  return mod.downloadHomePlan(params);
};

const downloadMasterPlan: DownloadInitializer<DownloadMasterPlanParams | DownloadPlanSetTemplateParams> = async (
  params,
) => {
  const mod = await import("lib/workers/pdfDownloads");
  return mod.downloadMasterPlan(params);
};

const downloadCommunityPlan: DownloadInitializer<BasePlanSetDownloadParams> = async (params) => {
  const mod = await import("lib/workers/pdfDownloads");
  return mod.downloadCommunityPlan(params);
};

const downloadPlanSetTemplate: DownloadInitializer<BasePlanSetDownloadParams> = async (params) => {
  const mod = await import("lib/workers/pdfDownloads");
  return mod.downloadPlanSetTemplate(params);
};

function DownloadHomePlanModalInternal({
  open,
  onClose,
  plan,
  titleSheetInfo,
  lotDiscounts,
  homePlanDiscounts,
}: DownloadHomePlanModalProps) {
  const { builder, enabledFeatures, user } = useUserContext();

  const { location } = useContext(HomePlanContext);
  const { planSetTemplateIdentifier, parameterValueOverrides, filterDisabledOptionSetOverrides, exportFileName } =
    useDownloadPlanModalContext();
  const { data: locationHierarchy, isLoading: locationHierarchyLoading } = useLocationHierarchy(
    builder?.id ?? "",
    location?.id ?? "",
  );
  const sendHomePlanAnalytics = useExportHomePlanEvents(plan, planSetTemplateIdentifier, locationHierarchy);
  const isMultiUnitFullBuildingPlan = isMultiUnitLocation(location) && plan?.isMultiUnit;
  const resolvedDefaultExportFileName = computeDefaultPlanSetExportFileName({
    doc: ensureDefined(plan).doc,
    planSetTemplateIdentifier,
    exportContextName: ensureDefined(plan).planSetId,
  });

  assertIsNotUndefined(builder);

  const locationAncestors = locationAncestorsForLocationId(locationHierarchy, location?.id);
  const waitingForLocationHierarchy = Boolean(location?.id) && locationHierarchyLoading;

  const titleSheetInfoWithLocationHierarchy = useMemo(
    () => ({
      ...titleSheetInfo,
      locationAncestors,
    }),
    [titleSheetInfo, locationAncestors],
  );

  if (waitingForLocationHierarchy) {
    return null;
  }

  const downloadParams: DownloadHomePlanParams | DownloadPlanSetTemplateParams = {
    planType: "home_plan",
    builder,
    titleSheetInfo: titleSheetInfoWithLocationHierarchy,
    plan,
    planName: ensureDefined(plan).planSetId,
    planSetTemplateIdentifier,
    parameterValueOverrides,
    filterDisabledOptionSetOverrides,
    exportFileName: exportFileName || resolvedDefaultExportFileName,
    service: PlanSetComputeService.getInstance(),
    lotDiscounts,
    homePlanDiscounts,
    enabledFeatures,
  };

  return (
    <DownloadProgressPlanModal<DownloadHomePlanParams | DownloadPlanSetTemplateParams>
      open={!!open}
      entityLockedAt={titleSheetInfo?.homePlan?.lockedAt}
      tosVariant="footer"
      downloadParams={downloadParams}
      initDownload={planSetTemplateIdentifier.templateId ? downloadPlanSetTemplate : downloadHomePlan}
      onClose={onClose}
      size="large"
      documentType={
        enabledFeatures.userDefinedPlansets ? BuilderDocumentType.DOCUMENTATION : BuilderDocumentType.PLAN_SET
      }
      sendAnalytics={sendHomePlanAnalytics}
    >
      {ensureDefined(downloadParams.plan).doc && (
        <ExportPlanSetTemplateModalContent
          doc={ensureDefined(downloadParams.plan).doc}
          userId={user?.id}
          homePlanSalesAgentId={titleSheetInfo?.homePlan?.salesStaffId}
          locationSalesAgentId={location?.salesStaffId}
          exportContextName={ensureDefined(plan).planSetId}
          allowBuiltInPlanSetTemplate={!isMultiUnitFullBuildingPlan}
        />
      )}
    </DownloadProgressPlanModal>
  );
}

export function DownloadHomePlanModal(props: DownloadHomePlanModalProps) {
  return (
    <DownloadPlanModalContextProvider>
      <DownloadHomePlanModalInternal {...props} />
    </DownloadPlanModalContextProvider>
  );
}

function DownloadMasterPlanModalInternal({ open, onClose, plan }: DownloadMasterPlanModalParams) {
  const { builder, enabledFeatures } = useUserContext();
  const [titleSheetInfo, setTitleSheetInfo] = useState<TitleSheetInfo>({ builder });
  const [masterPlanHydrated, setMasterPlanHydrated] = useState<ClientMasterPlanHydrated | undefined>();
  const [loadingMasterPlan, setLoadingMasterPlan] = useState(true);
  const { planSetTemplateIdentifier, parameterValueOverrides, filterDisabledOptionSetOverrides, exportFileName } =
    useDownloadPlanModalContext();
  const sendMasterPlanAnalytics = useExportMasterPlanEvents(masterPlanHydrated, planSetTemplateIdentifier);
  const resolvedDefaultExportFileName = computeDefaultPlanSetExportFileName({
    doc: masterPlanHydrated?.doc,
    planSetTemplateIdentifier,
    exportContextName: masterPlanHydrated?.name ?? undefined,
  });

  useEffect(() => {
    void (async () => {
      if (!plan || !builder) {
        return;
      }

      const [masterPlanHydrated, err] = await getMasterPlanHydrated(builder.id, ensureDefined(plan.id));

      if (err) {
        console.error(err);
        return;
      }

      setTitleSheetInfo((prev) => ({
        ...prev,
        masterPlan: masterPlanHydrated,
        builder: builder,
      }));
      setMasterPlanHydrated(masterPlanHydrated);

      setLoadingMasterPlan(false);
    })();
  }, [plan, builder]);

  assertIsNotUndefined(builder);

  const downloadParams: DownloadMasterPlanParams | DownloadPlanSetTemplateParams = {
    planType: "master_plan",
    builder,
    titleSheetInfo,
    plan: masterPlanHydrated as ClientMasterPlanHydrated,
    planSetTemplateIdentifier,
    parameterValueOverrides,
    filterDisabledOptionSetOverrides,
    exportFileName: exportFileName || resolvedDefaultExportFileName,
    service: PlanSetComputeService.getInstance(),
    enabledFeatures,
  };

  return (
    <DownloadProgressPlanModal<DownloadMasterPlanParams | DownloadPlanSetTemplateParams>
      open={!!open}
      tosVariant="footer"
      downloadParams={downloadParams}
      initDownload={planSetTemplateIdentifier.templateId ? downloadPlanSetTemplate : downloadMasterPlan}
      onClose={onClose}
      size="large"
      documentType={BuilderDocumentType.PLAN_SET}
      sendAnalytics={sendMasterPlanAnalytics}
    >
      {!loadingMasterPlan && (
        <ExportPlanSetTemplateModalContent
          doc={ensureDefined(downloadParams.plan).doc}
          exportContextName={masterPlanHydrated?.name ?? undefined}
        />
      )}
    </DownloadProgressPlanModal>
  );
}

export function DownloadMasterPlanModal(props: DownloadMasterPlanModalParams) {
  return (
    <DownloadPlanModalContextProvider>
      <DownloadMasterPlanModalInternal {...props} />
    </DownloadPlanModalContextProvider>
  );
}

function DownloadCommunityPlanModalInternal({ onClose, locationPlan, location }: DownloadCommunityPlanModalProps) {
  const { builder, enabledFeatures } = useUserContext();
  const { data: locationHierarchy, isLoading: locationHierarchyLoading } = useLocationHierarchy(
    builder?.id ?? "",
    location?.id ?? "",
  );
  const [locationPlanHydrated, setCommunityPlanHydrated] = useState<ClientLocationPlanHydrated | undefined>();
  const { planSetTemplateIdentifier, parameterValueOverrides, filterDisabledOptionSetOverrides, exportFileName } =
    useDownloadPlanModalContext();
  const sendCommunityPlanAnalytics = useExportCommunityPlanEvents(locationPlanHydrated, planSetTemplateIdentifier);
  const resolvedDefaultExportFileName = computeDefaultPlanSetExportFileName({
    doc: locationPlanHydrated?.doc,
    planSetTemplateIdentifier,
    exportContextName: location?.name,
  });

  const locationAncestors = locationAncestorsForLocationId(locationHierarchy, location?.id);

  const titleSheetInfo: TitleSheetInfo = useMemo(
    () => ({
      communityPlan: locationPlan,
      builder,
      community: location,
      locationAncestors,
    }),
    [locationPlan, builder, location, locationAncestors],
  );

  useEffect(() => {
    void (async () => {
      if (!builder || !locationPlan) {
        return;
      }

      assertIsNotNullOrUndefined(locationPlan.locationId);

      const [resp, err] = await getHydratedLocationPlan(builder.id, locationPlan.locationId, locationPlan.id);

      if (err) {
        console.error(err);
        return;
      }

      setCommunityPlanHydrated(resp);
    })();
  }, [locationPlan, builder]);

  const waitingForLocationHierarchy = Boolean(location?.id) && locationHierarchyLoading;

  if (!builder || !locationPlanHydrated || waitingForLocationHierarchy) {
    return null;
  }

  const downloadParams: DownloadCommunityPlanParams = {
    planType: "community_plan",
    builder,
    titleSheetInfo,
    plan: locationPlanHydrated,
    planSetTemplateIdentifier,
    parameterValueOverrides,
    filterDisabledOptionSetOverrides,
    exportFileName: exportFileName || resolvedDefaultExportFileName,
    service: PlanSetComputeService.getInstance(),
    enabledFeatures,
  };

  return (
    <DownloadProgressPlanModal<DownloadCommunityPlanParams | DownloadPlanSetTemplateParams>
      open
      tosVariant="footer"
      downloadParams={downloadParams}
      initDownload={planSetTemplateIdentifier.templateId ? downloadPlanSetTemplate : downloadCommunityPlan}
      onClose={onClose}
      size="large"
      documentType={BuilderDocumentType.PLAN_SET}
      sendAnalytics={sendCommunityPlanAnalytics}
    >
      <ExportPlanSetTemplateModalContent
        doc={ensureDefined(locationPlanHydrated.doc)}
        exportContextName={location?.name ?? undefined}
      />
    </DownloadProgressPlanModal>
  );
}

export function DownloadCommunityPlanModal({ onClose, locationPlan, location }: DownloadCommunityPlanModalProps) {
  return (
    <DownloadPlanModalContextProvider>
      <DownloadCommunityPlanModalInternal onClose={onClose} locationPlan={locationPlan} location={location} />
    </DownloadPlanModalContextProvider>
  );
}
